import { captureSentryException } from '@edulastic/common'
import { removeTokenFromHtml } from './helpers'

const openingTagRegex = /<([a-z]+)(?![^>]*\/>)[^>]*>/gi
const closingTagRegex = /<\/([a-z]+)>/gi

// Common self-closing HTML tags that don't need closing tags
const selfClosingTags = new Set([
  'br',
  'img',
  'hr',
  'input',
  'meta',
  'link',
  'area',
  'base',
  'col',
  'embed',
  'source',
  'track',
  'wbr',
])

/**
 * @param {*} sentencesArray
 * @returns sentencesArray with completed tags
 * Consider a span element like:
 * <span style="font-size: 14px;">first <span style="font-size: 20px;">second. </span> Second continue</span>first continue</span><br/>
 * When the sentence is split into sentence (split with . or br tag), the tags are not closed properly and
 * font-size and other tags is not applied correctly to the split sentences.
 * This function keeps a stack of open tags and appends to future sentences until the tag is closed.
 * This ensures styling is applied to the entire sentence.
 */

export const completeTagsForSentencesArray = (sentencesArray = []) => {
  const updatedSentencesArray =
    Array.isArray(sentencesArray) && sentencesArray?.length
      ? [...sentencesArray]
      : []
  if (updatedSentencesArray?.length) {
    const tagsStack = []
    ;(updatedSentencesArray || []).forEach((sentenceObj, sentenceIndex) => {
      try {
        let sentence = sentenceObj?.value || ''
        if (sentence) {
          // Normalize self-closing tags (e.g., <br/> to <br></br>) upfront
          // This ensures they're handled consistently and don't interfere with tag tracking
          sentence = sentence.replace(
            /<([a-z]+)([^>]*)\/>/gi,
            (match, tagName, attributes) => {
              if (selfClosingTags.has(tagName.toLowerCase())) {
                return `<${tagName}${attributes}></${tagName}>`
              }
              return match
            }
          )
          let updatedSentence = sentence

          if (tagsStack?.length) {
            updatedSentence = `${tagsStack
              .map((tag) => tag?.match || '')
              .join('')}${sentence}`
          }
          // Always update with normalized sentence (and prepended tags if any)
          updatedSentencesArray[sentenceIndex].value = updatedSentence

          const tagsMatches = []
          const openingTagMatches = sentence.matchAll(openingTagRegex)
          const closingTagsMatches = sentence.matchAll(closingTagRegex)

          for (const openingTagMatch of openingTagMatches) {
            const openingTag = openingTagMatch?.[0] || ''
            const openingTagName = openingTagMatch?.[1] || ''
            if (openingTag && openingTagName) {
              tagsMatches.push({
                type: 'OPEN',
                tagName: openingTagName,
                match: openingTag,
                index: openingTagMatch?.index,
              })
            }
          }

          for (const closingTagMatch of closingTagsMatches) {
            const closingTag = closingTagMatch?.[0] || ''
            const closingTagName = closingTagMatch?.[1] || ''
            if (closingTag && closingTagName) {
              tagsMatches.push({
                type: 'CLOSE',
                tagName: closingTagName,
                match: closingTag,
                index: closingTagMatch?.index,
              })
            }
          }

          tagsMatches.sort((a, b) => a.index - b.index)

          for (const tagMatch of tagsMatches) {
            if (
              tagMatch?.type === 'OPEN' &&
              tagMatch?.match &&
              tagMatch?.tagName
            ) {
              // Skip self-closing tags - they don't need to be tracked in the stack
              if (!selfClosingTags.has(tagMatch.tagName.toLowerCase())) {
                tagsStack.push({
                  match: tagMatch.match,
                  tagName: tagMatch?.tagName,
                })
              }
            } else if (
              tagMatch.type === 'CLOSE' &&
              !selfClosingTags.has(tagMatch.tagName?.toLowerCase()) &&
              tagsStack?.length &&
              tagsStack?.[tagsStack.length - 1]?.tagName === tagMatch?.tagName
            ) {
              // Only process closing tags for non-self-closing tags
              tagsStack.pop()
            }
          }
        }
      } catch (error) {
        captureSentryException(error, {
          errorMessage:
            'Error in completing tags for sentences array in TokenHighlight',
        })
      }
    })
  }
  return updatedSentencesArray
}

/**
 * Extracts and processes tokens from a container element
 * Finds all active token spans, extracts their content, and builds a tokens array
 * with active and inactive segments. Normalizes HTML and completes tags.
 *
 * @param {HTMLElement} container - The DOM container element to extract tokens from
 * @returns {Array<{value: string, active: boolean}>} Array of tokens with value and active status
 */
export const extractAndProcessTokensFromContainer = (container) => {
  if (!container) {
    return []
  }

  const tokens = []
  const html = container.innerHTML

  // Find all token spans using querySelectorAll (handles nested tokens)
  const tokenSpans = container.querySelectorAll('span.token.active-word')

  if (tokenSpans.length === 0) {
    // No tokens found, return entire content as one inactive token
    tokens.push({ value: removeTokenFromHtml(html), active: false })
  } else {
    // Extract token contents and their outerHTML for replacement
    const tokenData = Array.from(tokenSpans).map((span) => ({
      outerHTML: span.outerHTML,
      content: removeTokenFromHtml(span.innerHTML),
    }))

    // Replace each token with a unique marker to split by
    let processedHtml = html
    const markers = []
    tokenData.forEach((token, index) => {
      const marker = `__TOKEN_MARKER_${index}__`
      markers.push(marker)
      processedHtml = processedHtml.replace(token.outerHTML, marker)
    })

    // Split by markers to get content segments
    const parts = processedHtml.split(/(__TOKEN_MARKER_\d+__)/)

    // Build tokens array
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]
      const markerMatch = part.match(/^__TOKEN_MARKER_(\d+)__$/)

      if (markerMatch) {
        // This is a marker, add the corresponding active token
        const tokenIndex = parseInt(markerMatch[1], 10)
        tokens.push({ value: tokenData[tokenIndex].content, active: true })
      } else if (part) {
        // This is content between tokens, add as inactive token
        const inactiveValue = removeTokenFromHtml(part)
        if (inactiveValue) {
          tokens.push({ value: inactiveValue, active: false })
        }
      }
    }
  }

  return [...tokens]
}
